/**
 * Visual floor height for the toon renderer (the sim stays 2D). The renderer installs the active
 * dungeon's height function on init (`@keystone/content` groundHeightAt) and every system that
 * puts something on the ground (units, blob shadows, telegraphs, fx, rings, camera, picking,
 * nameplate projection) reads it through `groundY`, so everything sits on the right platform.
 *
 * Module state, not per-renderer: only one toon renderer draws at a time (hot swaps dispose
 * the old one first). Flat dungeons install nothing and pay one function call returning 0.
 */
import * as THREE from 'three';
import type { DungeonDef, Vec2 } from '@keystone/sim';
import { groundHeightAt } from '@keystone/content';

export type GroundFn = (x: number, y: number) => number;

const FLAT: GroundFn = () => 0;
let fn: GroundFn = FLAT;
let flat = true;

/** Ground height (yards) at sim position (x, y). */
export function groundY(x: number, y: number): number {
  return flat ? 0 : fn(x, y);
}

/** True when the installed dungeon has no elevation anywhere (callers can skip conforming). */
export function groundIsFlat(): boolean {
  return flat;
}

/** Install a custom height function (tests, previews) or reset to flat with null. */
export function setGroundFn(f: GroundFn | null): void {
  fn = f ?? FLAT;
  flat = !f;
}

/** Whether a dungeon has any visual elevation (room levels or corridor rises). */
export function dungeonHasElevation(d: DungeonDef | undefined): boolean {
  if (!d) return false;
  return d.rooms.some((r) => (r.elevation ?? 0) !== 0) || d.corridors.some((c) => (c.rises?.length ?? 0) > 0);
}

/** Install the dungeon's height function (flat dungeons keep the zero fast path). */
export function setGroundForDungeon(d: DungeonDef | undefined): void {
  if (!d || !dungeonHasElevation(d)) {
    setGroundFn(null);
    return;
  }
  setGroundFn((x, y) => groundHeightAt(d, x, y));
}

/**
 * Exponential smoothing for a height that follows the ground (camera focus). Snaps on big jumps
 * (teleports) so the camera never slides through a level for a second.
 */
export function smoothHeight(current: number, target: number, dtMs: number, tauMs = 140, snapAt = 6): number {
  if (!Number.isFinite(current) || Math.abs(target - current) > snapAt || dtMs <= 0) return target;
  return current + (target - current) * (1 - Math.exp(-dtMs / tauMs));
}

/** three.js world point for a sim position `height` yards above the floor there. */
export function groundPoint(p: Vec2, height: number, out: THREE.Vector3): THREE.Vector3 {
  return out.set(p.x, height + groundY(p.x, p.y), -p.y);
}

/**
 * Sim position + height above the floor → normalized device coords (nameplates, combat text).
 * Elevation-aware: the floor under `p` is added before projecting.
 */
export function projectGround(p: Vec2, height: number, camera: THREE.Camera, out: THREE.Vector3): THREE.Vector3 {
  return groundPoint(p, height, out).project(camera);
}

const PICK_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const PICK_HIT = new THREE.Vector3();

/**
 * Ray → ground point on an elevated floor: intersect the plane at the current guess height,
 * re-read the floor height there and repeat (converges in 2–3 steps; on a cliff edge the last
 * consistent hit wins).
 */
export function rayToGround(ray: THREE.Ray, guess = 0): Vec2 | null {
  let h = Number.isFinite(guess) ? guess : 0;
  let best: Vec2 | null = null;
  for (let i = 0; i < 4; i++) {
    PICK_PLANE.constant = -h;
    if (!ray.intersectPlane(PICK_PLANE, PICK_HIT)) break;
    const p = { x: PICK_HIT.x, y: -PICK_HIT.z };
    const g = groundY(p.x, p.y);
    best = p;
    if (Math.abs(g - h) < 0.02) break;
    h = g;
  }
  return best;
}

/** +glide: where an object stands: its own height (a sky chest on a platform), else the ground. */
export function objY(o: { pos: { x: number; y: number }; z?: number }): number {
  return o.z ?? groundY(o.pos.x, o.pos.y);
}
